export type QuickBooksSyncStatus = 'PENDING_SYNC' | 'SYNCED' | 'SYNC_FAILED';
export type QuickBooksInvoiceState = 'ACTIVE' | 'VOIDED' | 'NOT_FOUND';

export type EventInvoiceLine = {
  service_name: string;
  done_count: number;
};

export type EventInvoicePreview = {
  lines: EventInvoiceLine[];
};

export type EventInvoiceSummary = {
  event_invoice_id: number;
  quickbooks_sync_status: QuickBooksSyncStatus;
  qb_doc_number?: string | null;
  quickbooks_invoice_state?: QuickBooksInvoiceState | null;
  error_message?: string | null;
};

export type EventInvoicePreviewResponse = {
  preview: EventInvoicePreview;
  invoice?: EventInvoiceSummary | null;
};

export type EventInvoiceDraftResponse = EventInvoiceSummary & {
  was_existing: boolean;
  preview: EventInvoicePreview;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isQuickBooksSyncStatus = (value: unknown): value is QuickBooksSyncStatus =>
  value === 'PENDING_SYNC' || value === 'SYNCED' || value === 'SYNC_FAILED';

const isInvoiceLine = (value: unknown): value is EventInvoiceLine => {
  if (!isRecord(value)) return false;
  return (
    typeof value.service_name === 'string' &&
    typeof value.done_count === 'number' &&
    Number.isInteger(value.done_count) &&
    value.done_count >= 0
  );
};

export const isEventInvoicePreview = (value: unknown): value is EventInvoicePreview => {
  if (!isRecord(value)) return false;
  return Array.isArray(value.lines) && value.lines.every(isInvoiceLine);
};

export const isEventInvoiceSummary = (value: unknown): value is EventInvoiceSummary => {
  if (!isRecord(value)) return false;
  const invoiceState = value.quickbooks_invoice_state;
  return (
    typeof value.event_invoice_id === 'number' &&
    isQuickBooksSyncStatus(value.quickbooks_sync_status) &&
    (invoiceState === undefined ||
      invoiceState === null ||
      invoiceState === 'ACTIVE' ||
      invoiceState === 'VOIDED' ||
      invoiceState === 'NOT_FOUND')
  );
};

export const parseInvoicePreviewResponse = (value: unknown): EventInvoicePreviewResponse | null => {
  if (!isRecord(value) || !isEventInvoicePreview(value.preview)) {
    return null;
  }
  if (value.invoice !== undefined && value.invoice !== null && !isEventInvoiceSummary(value.invoice)) {
    return null;
  }
  return value as EventInvoicePreviewResponse;
};

export const parseInvoiceDraftResponse = (value: unknown): EventInvoiceDraftResponse | null => {
  if (!isRecord(value)) {
    return null;
  }
  if (
    typeof value.was_existing !== 'boolean' ||
    !isEventInvoicePreview(value.preview) ||
    !isEventInvoiceSummary(value)
  ) {
    return null;
  }
  return value as EventInvoiceDraftResponse;
};

export const invoiceErrorMessage = (body: unknown, statusCode?: number): string => {
  if (isRecord(body)) {
    const detail = body.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail.trim();
    }
    const message = body.message;
    if (typeof message === 'string' && message.trim()) {
      return message.trim();
    }
  }

  if (statusCode === 403) return 'You do not have permission to invoice this event.';
  if (statusCode === 404) return 'The event or its account could not be found.';
  if (statusCode === 409) return 'The invoice cannot be created in its current state.';
  if (statusCode === 422) return 'The event or QuickBooks configuration is not ready for invoicing.';
  if (statusCode === 502 || statusCode === 503) {
    return 'QuickBooks is temporarily unavailable. Please try again.';
  }
  return 'Invoice processing failed. Please try again.';
};
